import * as THREE from "three";
import type { EnemyFaction, MonsterSize } from "../types/entity.js";
import { factionColor, factionRimColor } from "../types/factionColor.js";
import type { Position } from "../types/grid.js";
import { subCellOffset } from "../grid/packRules.js";
import { INTERACTIVE_LAYER } from "../renderer/Raycaster.js";
import { spriteUrl } from "../adventure/ActiveAdventure.js";
import {
  loadAuthoredSprite,
  loadAnimation,
  cardinalFromTo,
  selectView,
  pickFrame,
  alignKey,
  animationForEnemyState,
  type LoadedSprite,
  type LoadedAnimation,
  type SpriteFacing,
  type SpriteViewTag,
  type SpriteAnimationName,
  type SpriteSizeClass,
} from "./spriteSchema.js";
import { EventBus } from "../EventBus.js";
import { cellOccupancyStore } from "../grid/cellOccupancyStore.js";

// ── Constants ─────────────────────────────────────────────────────────────────

const DEATH_FADE_DURATION = 0.5; // seconds
const SPRITE_Y = 0.6; // world-space height of sprite center (procedural / flat fallback)
const SHADOW_Y = 0.01; // just above the floor

// E-pack — procedural fallback dims for `small` enemies. Roughly 60% of the
// medium silhouette so a 4-pack of circles reads as four distinct critters
// instead of one overlapping blob; center height keeps feet on the floor.
const SPRITE_Y_SMALL = 0.38;
const SMALL_SCALE = { x: 0.5, y: 0.72 } as const;
const SHADOW_RADIUS = 0.3;
const SHADOW_RADIUS_SMALL = 0.17;

/**
 * A06 — fake drop shadows are gated behind this constant. With PBR floors and
 * billboarded sprites in the same frame, the flat dark circles read as
 * pasted-on stickers. Geometry/material construction stays so re-enabling is
 * a one-line flip; a proper sprite-shadow pass is deferred (see spec).
 */
const SPRITE_SHADOWS_ENABLED = false;

/**
 * Per-size-class billboard dimensions for authored sprites.
 *
 * Cell height is 1 world unit (floor at y=0, ceiling at y=1), so a sprite
 * centered at `y` with vertical `sy` spans `y ± sy/2`. Anything that exceeds
 * 1.0 on the top edge clips through the ceiling — these numbers keep the
 * head inside the cell with a small margin. `sizeClass` is set by the sprite
 * author in `sprite.json`; absent manifests keep the legacy (0.8, 1.2)
 * procedural/flat scale in `spawnEnemy`/`spawnNpc`.
 */
function billboardDims(sizeClass: SpriteSizeClass | undefined): {
  sx: number;
  sy: number;
  y: number;
} {
  switch (sizeClass) {
    case "half":
      return { sx: 0.55, sy: 0.75, y: 0.4 };
    case "flying":
      return { sx: 0.65, sy: 0.85, y: 0.65 };
    case "full":
    default:
      return { sx: 0.7, sy: 0.95, y: 0.5 };
  }
}

/**
 * A01c — duration of the red hit-flash tint applied on `combat:damage_taken`.
 * Short enough to feel snappy but long enough to read at 60fps during a
 * fast exchange. The tint is a multiplicative color on `SpriteMaterial`,
 * so it layers correctly over procedural, A00 flat, and A01a/b authored
 * textures without rebuilding the texture.
 */
const HIT_FLASH_DURATION = 0.12;
const HIT_FLASH_COLOR = 0xff6666;

// ── Types ─────────────────────────────────────────────────────────────────────

interface SpriteRecord {
  sprite: THREE.Sprite;
  shadow: THREE.Mesh;
  dying: boolean;
  fadeTimer: number;
  /** Only populated for NPC sprites (N09). */
  npcId?: string;
  npcState?: NpcSpriteState;
  /** Entity data-id (monsterId / npcId) — reused if an authored PNG arrives after spawn. */
  entityId: string;
  /** For enemies: faction governs rim color. */
  faction?: EnemyFaction;
  /** A01a — set when the entity has a directional sprite manifest on disk. */
  authored?: LoadedSprite;
  /** A01a — entity facing; defaults to facing the camera on spawn. */
  facing: SpriteFacing;
  /** A01a — last view rendered, kept so `tick` can skip unchanged frames. */
  lastView?: { tag: SpriteViewTag; mirror: boolean };
  /**
   * A01b — current animation name. Drives which `LoadedAnimation` the
   * renderer pulls frames from. Swapped by `setAnimation` in response
   * to `enemy:state_changed` and `narrative:*` events; resets the frame
   * clock on change so the new loop starts on frame 0.
   */
  currentAnim: SpriteAnimationName;
  /** A01b — integer frame index advanced by `tick`. */
  frameIndex: number;
  /** A01b — time accumulated toward the next frame (seconds). */
  frameTimer: number;
  /** A01b — last rendered frame, kept so `tick` can skip unchanged frames. */
  lastFrame: number | undefined;
  /** A01b — animations actively being lazy-loaded; prevents duplicate fetches. */
  pendingAnims?: Set<SpriteAnimationName>;
  /**
   * A01c — remaining seconds on the red hit-flash tint. 0 means no flash.
   * `tick` decrements and restores `material.color` when it crosses zero.
   */
  hitFlashTimer: number;
  /**
   * True while the entity's machine is in combat/attacking. Drives the
   * combat-only faction rim: exploring sprites render clean art, engaged
   * enemies get a muted outline. Toggled by `enemy:state_changed`.
   */
  engaged: boolean;
  /**
   * E-pack — the entity's logical grid cell. Facing on movement is derived
   * from cell-to-cell deltas (sprite.position now carries sub-cell offsets,
   * so it can no longer double as the previous cell).
   */
  cell: Position;
  /** E-pack — sub-cell world offset for pack members; {0,0} when alone. */
  subCell: { dx: number; dz: number };
  /** E-pack — size class driving the procedural fallback dims. */
  size?: MonsterSize;
}

/**
 * NPC sprite states (N09). Once an NPC flips to HOSTILE the sprite joins
 * the enemy visual path — the rim renders red and the sprite's `userData`
 * is no longer treated as an NPC by the interaction dispatcher (the
 * faction flip is the canonical handoff).
 */
export type NpcSpriteState = "idle" | "talking" | "hostile";

// ── Procedural texture ────────────────────────────────────────────────────────

function hashHue(monsterId: string): number {
  return [...monsterId].reduce((h, c) => h + c.charCodeAt(0), 0) % 360;
}

interface RimStyle {
  color: string;
  width: number;
  /** Rim layer opacity 0..1 (default 1). Applied once over the composed rim, not per stamp. */
  opacity?: number;
}

/**
 * Shared circle-with-letter procedural texture (E13/N09). Entity kind picks
 * the fill hue + rim style; the geometry, font, and letter placement are
 * identical across enemy and NPC variants to keep a consistent silhouette
 * until the X15 sprite-sheet pipeline replaces all of this.
 */
function drawSpriteCircle(
  letterSource: string,
  fill: { hue: number; saturation: number; lightness: number },
  rim: RimStyle | null,
): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = `hsl(${fill.hue}, ${fill.saturation}%, ${fill.lightness}%)`;
  ctx.beginPath();
  ctx.arc(32, 32, 28, 0, Math.PI * 2);
  ctx.fill();

  if (rim) {
    ctx.globalAlpha = rim.opacity ?? 1;
    ctx.strokeStyle = rim.color;
    ctx.lineWidth = rim.width;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  ctx.fillStyle = "white";
  ctx.font = "bold 32px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(letterSource.charAt(0).toUpperCase(), 32, 34);

  const tex = new THREE.CanvasTexture(canvas);
  // Canvas pixels are sRGB. Without the tag the renderer treats them as
  // linear and gamma-lifts them on output — washed-out, hue-shifted
  // sprites against color-correct walls (MaterialFactory tags its
  // textures; sprites must match).
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * NPC sprite texture for a given state (N09). Quiet-rim policy: idle NPCs
 * render clean; the active speaker gets a muted neutral rim; a
 * HOSTILE-flipped NPC gets the muted hostile rim so it matches engaged
 * enemies. Full sprite-sheet animation is deferred to X15.
 */
function makeNpcTexture(npcId: string, state: NpcSpriteState): THREE.CanvasTexture {
  const rim: RimStyle | null =
    state === "hostile"
      ? { color: factionRimColor("hostile"), width: 2, opacity: 0.65 }
      : state === "talking"
        ? { color: factionRimColor("neutral"), width: 2, opacity: 0.65 }
        : null;
  return drawSpriteCircle(
    npcId,
    { hue: (hashHue(npcId) + 40) % 360, saturation: 55, lightness: 55 },
    rim,
  );
}

function makeProceduralTexture(monsterId: string, faction: EnemyFaction): THREE.CanvasTexture {
  // Faction-colored rim (E13), muted. Procedural letter-circles keep an
  // always-on rim — the circle IS placeholder art, faction is its main
  // signal, and this path never rebuilds on combat-state changes.
  return drawSpriteCircle(
    monsterId,
    { hue: hashHue(monsterId), saturation: 60, lightness: 45 },
    { color: factionRimColor(faction), width: 3, opacity: 0.8 },
  );
}

// ── Authored sprite loader (A00) ─────────────────────────────────────────────

type AuthoredImageState = HTMLImageElement | "failed";

/**
 * Per-entity image cache so repeated spawns of the same monster reuse the
 * same decoded `HTMLImageElement`. Values:
 *   - `HTMLImageElement` — PNG loaded successfully
 *   - `"failed"` — PNG absent or errored; don't retry
 */
const authoredImages = new Map<string, AuthoredImageState>();
const pendingLoads = new Map<string, Promise<HTMLImageElement | null>>();

/** A globally-overridable loader hook so tests can simulate PNG presence. */
let loaderOverride: ((url: string) => Promise<HTMLImageElement | null>) | null = null;

export function _setSpriteLoaderForTests(
  override: ((url: string) => Promise<HTMLImageElement | null>) | null,
): void {
  loaderOverride = override;
  authoredImages.clear();
  pendingLoads.clear();
}

function defaultLoader(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function loadAuthoredImage(entityId: string): Promise<HTMLImageElement | null> {
  const cached = authoredImages.get(entityId);
  if (cached === "failed") return Promise.resolve(null);
  if (cached) return Promise.resolve(cached);
  const inflight = pendingLoads.get(entityId);
  if (inflight) return inflight;

  const loader = loaderOverride ?? defaultLoader;
  const p = loader(spriteUrl(entityId)).then((img) => {
    pendingLoads.delete(entityId);
    if (img) {
      authoredImages.set(entityId, img);
      return img;
    }
    authoredImages.set(entityId, "failed");
    return null;
  });
  pendingLoads.set(entityId, p);
  return p;
}

/**
 * A01a — mirror a source image horizontally onto a fresh 128×192 canvas so
 * `makeAuthoredTexture` can treat the mirrored output like a regular image.
 * Called when the camera is off the entity's left-hand side and we want to
 * reuse the single authored `*_side_*` PNG for the opposite flank.
 */
function mirrorHorizontally(src: CanvasImageSource): HTMLCanvasElement {
  const w = 128;
  const h = 192;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.save();
  ctx.translate(w, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(src, 0, 0, w, h);
  ctx.restore();
  return canvas;
}

/**
 * Compose an authored portrait (image or canvas) with a silhouette rim in
 * the faction color. Offset-tint trick: draw the sprite 8 times at ±width
 * offsets, tint each copy to the rim color via `source-in`, then draw the
 * unmodified sprite on top.
 */
function makeAuthoredTexture(
  img: CanvasImageSource,
  rim: RimStyle | null,
  align: { x: number; y: number } = { x: 0, y: 0 },
): THREE.CanvasTexture {
  const w = 128;
  const h = 192;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;

  const tmp = document.createElement("canvas");
  tmp.width = w;
  tmp.height = h;
  const tctx = tmp.getContext("2d")!;

  const off = Math.max(1, Math.round(rim?.width ?? 0));
  const offsets: ReadonlyArray<readonly [number, number]> = [
    [-off, 0],
    [off, 0],
    [0, -off],
    [0, off],
    [-off, -off],
    [off, -off],
    [-off, off],
    [off, off],
  ];

  // A04 — alignment shifts both the rim passes and the final image by the
  // same amount so the outline tracks the body when the author nudges a
  // frame into place.
  const ax = align.x;
  const ay = align.y;

  if (rim) {
    // Accumulate the 8 offset stamps on their own canvas, then composite
    // the finished rim ONCE at the rim's opacity — stamping each pass
    // directly at partial alpha would compound where stamps overlap.
    const rimAcc = document.createElement("canvas");
    rimAcc.width = w;
    rimAcc.height = h;
    const rctx = rimAcc.getContext("2d")!;
    for (const [dx, dy] of offsets) {
      tctx.globalCompositeOperation = "source-over";
      tctx.clearRect(0, 0, w, h);
      tctx.drawImage(img, dx + ax, dy + ay, w, h);
      tctx.globalCompositeOperation = "source-in";
      tctx.fillStyle = rim.color;
      tctx.fillRect(0, 0, w, h);
      rctx.drawImage(tmp, 0, 0);
    }
    ctx.globalAlpha = rim.opacity ?? 1;
    ctx.drawImage(rimAcc, 0, 0);
    ctx.globalAlpha = 1;
  }
  ctx.drawImage(img, ax, ay, w, h);

  const tex = new THREE.CanvasTexture(canvas);
  // sRGB tag — see drawSpriteCircle. Authored PNGs are sRGB; without
  // this the in-game sprite reads duller and hue-shifted vs the art.
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

// ── SpriteManager ─────────────────────────────────────────────────────────────

/**
 * Manages billboarded `THREE.Sprite` entities for monsters.
 *
 * Each enemy gets:
 *  - A `THREE.Sprite` on INTERACTIVE_LAYER (Layer 1) so the raycaster can
 *    pick it up for click-to-attack.
 *  - A flat `THREE.Mesh` shadow circle on Layer 0 to anchor it to the floor.
 *
 * On death, `startDeathAnimation()` fades the sprite and shadow out over
 * DEATH_FADE_DURATION seconds then removes them from the scene.
 */
export class SpriteManager {
  private readonly scene: THREE.Scene;
  private readonly sprites = new Map<string, SpriteRecord>();
  /** A01a — camera ref used by the per-tick directional view picker. */
  private readonly camera: THREE.Camera | null;
  /** A01b — unsubscribe handles for EventBus wiring; released in `dispose`. */
  private readonly unsubscribes: Array<() => void> = [];

  constructor(scene: THREE.Scene, camera?: THREE.Camera) {
    this.scene = scene;
    this.camera = camera ?? null;

    // A01b — state → animation wiring. SpriteManager intentionally
    // consumes events rather than holding CombatManager / NarrativeManager
    // references so the engine → sprites dependency stays one-way.
    this.unsubscribes.push(
      EventBus.on("enemy:state_changed", ({ instanceId, state }) => {
        this.setAnimation(instanceId, animationForEnemyState(state));
        const record = this.sprites.get(instanceId);
        if (record) {
          const engaged = state === "combat" || state === "attacking";
          if (record.engaged !== engaged) {
            record.engaged = engaged;
            // Invalidate the view cache so the next tick re-composites
            // the texture with the rim added or removed.
            delete record.lastView;
            record.lastFrame = undefined;
          }
        }
      }),
      EventBus.on("narrative:start", ({ speakerInstanceId }) => {
        if (speakerInstanceId !== undefined) {
          this.setAnimation(speakerInstanceId, "talk");
        }
      }),
      EventBus.on("narrative:ended", () => {
        // No speaker in the payload — return every NPC we bumped into
        // `talk` back to `idle`. Enemies are untouched; their animation
        // is driven by `enemy:state_changed`.
        for (const [id, record] of this.sprites) {
          if (record.npcId !== undefined && record.currentAnim === "talk") {
            this.setAnimation(id, "idle");
          }
        }
      }),
      // A01c — damage flash. `combat:damage_taken` also fires for party
      // members (who have no sprite); the lookup miss is a silent no-op.
      EventBus.on("combat:damage_taken", ({ instanceId }) => {
        this.triggerHitFlash(instanceId);
      }),
    );
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  spawnEnemy(
    instanceId: string,
    monsterId: string,
    pos: Position,
    faction: EnemyFaction = "hostile",
    size?: MonsterSize,
  ): void {
    const texture = makeProceduralTexture(monsterId, faction);
    const small = size === "small";

    // Billboard sprite on INTERACTIVE_LAYER
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    if (small) sprite.scale.set(SMALL_SCALE.x, SMALL_SCALE.y, 1);
    else sprite.scale.set(0.8, 1.2, 1);
    sprite.position.set(pos.x, small ? SPRITE_Y_SMALL : SPRITE_Y, pos.z);
    sprite.layers.set(INTERACTIVE_LAYER);
    sprite.userData = { instanceId, kind: "enemy", faction };
    this.scene.add(sprite);

    // Shadow circle on Layer 0
    const shadowGeo = new THREE.CircleGeometry(small ? SHADOW_RADIUS_SMALL : SHADOW_RADIUS, 8);
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.4,
    });
    const shadow = new THREE.Mesh(shadowGeo, shadowMat);
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.set(pos.x, SHADOW_Y, pos.z);
    if (SPRITE_SHADOWS_ENABLED) this.scene.add(shadow);

    const record: SpriteRecord = {
      sprite,
      shadow,
      dying: false,
      fadeTimer: 0,
      entityId: monsterId,
      faction,
      facing: this.initialFacing(pos),
      engaged: false,
      currentAnim: "idle",
      frameIndex: 0,
      frameTimer: 0,
      lastFrame: undefined,
      hitFlashTimer: 0,
      cell: { x: pos.x, z: pos.z },
      subCell: { dx: 0, dz: 0 },
      ...(size !== undefined ? { size } : {}),
    };
    this.sprites.set(instanceId, record);

    // A01a → A00: probe for a directional manifest first, then fall back
    // to the flat PNG, then to the procedural texture already in place.
    void this.tryUpgradeToAuthored(instanceId, monsterId, () => this.enemyRim(instanceId));
  }

  /**
   * Spawn an NPC billboard sprite on INTERACTIVE_LAYER.
   * Click events on this sprite should start the named Ink knot, not combat.
   */
  spawnNpc(
    instanceId: string,
    npcId: string,
    pos: Position,
    inkKnot: string,
    portrait?: string,
  ): void {
    const texture = makeNpcTexture(npcId, "idle");
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(0.8, 1.2, 1);
    sprite.position.set(pos.x, SPRITE_Y, pos.z);
    sprite.layers.set(INTERACTIVE_LAYER);
    sprite.userData = { instanceId, kind: "npc", inkKnot, ...(portrait ? { portrait } : {}) };
    this.scene.add(sprite);

    // E16: NPCs hard-block player movement; bumping them fires the same
    // ink knot a click would (wired in main.tsx).
    cellOccupancyStore.place(instanceId, pos);

    const shadowGeo = new THREE.CircleGeometry(0.3, 8);
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.4,
    });
    const shadow = new THREE.Mesh(shadowGeo, shadowMat);
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.set(pos.x, SHADOW_Y, pos.z);
    if (SPRITE_SHADOWS_ENABLED) this.scene.add(shadow);

    this.sprites.set(instanceId, {
      sprite,
      shadow,
      dying: false,
      fadeTimer: 0,
      entityId: npcId,
      npcId,
      npcState: "idle",
      facing: this.initialFacing(pos),
      engaged: false,
      currentAnim: "idle",
      frameIndex: 0,
      frameTimer: 0,
      lastFrame: undefined,
      hitFlashTimer: 0,
      cell: { x: pos.x, z: pos.z },
      subCell: { dx: 0, dz: 0 },
    });

    // A01a → A00: probe for a directional manifest first, then fall back
    // to the flat PNG.
    void this.tryUpgradeToAuthored(instanceId, npcId, () => this.npcRim(instanceId));
  }

  /**
   * A01a — override an entity's facing direction. Enemy AI and narrative
   * flows use this (e.g. face the player when alerted, face the speaker
   * during dialogue). The next `tick` redraws the view if it changed.
   */
  setFacing(instanceId: string, facing: SpriteFacing): void {
    const record = this.sprites.get(instanceId);
    if (!record) return;
    record.facing = facing;
  }

  /**
   * Compute the initial facing for a newly-spawned sprite: look toward the
   * camera so the default view is `front`. Falls back to "S" when no camera
   * is attached (tests).
   */
  private initialFacing(pos: Position): SpriteFacing {
    if (!this.camera) return "S";
    const cam = this.camera.position;
    return cardinalFromTo(pos, { x: cam.x, z: cam.z });
  }

  /**
   * Update an NPC's sprite state (N09). Regenerates the procedural texture
   * with the state's rim color. No-op when the sprite isn't an NPC or is
   * already in the requested state.
   *
   * Transition to `hostile` also flips the sprite's `userData.kind` to
   * `"enemy"` so the interaction dispatcher routes clicks to attack
   * rather than dialogue — the narrative and combat paths stay decoupled.
   */
  setNpcState(instanceId: string, state: NpcSpriteState): void {
    const record = this.sprites.get(instanceId);
    if (!record || record.npcId === undefined) return;
    if (record.npcState === state) return;

    record.npcState = state;
    if (state === "hostile") {
      const ud = record.sprite.userData as { kind: string; faction?: EnemyFaction };
      ud.kind = "enemy";
      ud.faction = "hostile";
    }

    const authored = authoredImages.get(record.entityId);
    const texture =
      authored && authored !== "failed"
        ? makeAuthoredTexture(authored, this.npcRim(instanceId))
        : makeNpcTexture(record.npcId, state);

    const material = record.sprite.material as THREE.SpriteMaterial;
    material.map?.dispose();
    material.map = texture;
    material.needsUpdate = true;
  }

  // ── A00 rim + upgrade helpers ─────────────────────────────────────────────

  /**
   * Combat-only quiet rim: clean art while exploring, a muted
   * faction-colored outline only while the enemy is engaged. The bright
   * always-on rim read as a neon sticker against the dungeon palette.
   */
  private enemyRim(instanceId: string): RimStyle | null {
    const record = this.sprites.get(instanceId);
    if (!record || !record.engaged) return null;
    const faction = record.faction ?? "hostile";
    return { color: factionRimColor(faction), width: 2, opacity: 0.65 };
  }

  private npcRim(instanceId: string): RimStyle | null {
    const record = this.sprites.get(instanceId);
    const state = record?.npcState ?? "idle";
    // A hostile-flipped NPC joins the enemy visual path: rim only while
    // its machine is actually engaged.
    if (state === "hostile") {
      return record?.engaged
        ? { color: factionRimColor("hostile"), width: 2, opacity: 0.65 }
        : null;
    }
    if (state === "talking") return { color: factionRimColor("neutral"), width: 2, opacity: 0.65 };
    return null;
  }

  private async tryUpgradeToAuthored(
    instanceId: string,
    entityId: string,
    rimFor: () => RimStyle | null,
  ): Promise<void> {
    // A01a: first try the directional manifest (`<entity>/sprite.json`).
    // Missing or malformed → fall through to the A00 flat `<entity>.png`.
    const authored = await loadAuthoredSprite(entityId);
    const record = this.sprites.get(instanceId);
    if (!record || record.dying) return;

    if (authored) {
      record.authored = authored;
      const dims = billboardDims(authored.manifest.sizeClass);
      record.sprite.scale.set(dims.sx, dims.sy, 1);
      record.sprite.position.y = dims.y;
      this.refreshFrame(instanceId, rimFor());
      return;
    }

    const img = await loadAuthoredImage(entityId);
    if (!img) return;
    const after = this.sprites.get(instanceId);
    if (!after || after.dying) return;
    // A00 flat PNGs composite once at load — under the combat-only rim
    // policy they render clean and stay clean (no rebuild on engagement).
    // No shipped enemy uses this path; directional manifests handle rims.
    const texture = makeAuthoredTexture(img, rimFor());
    const material = after.sprite.material as THREE.SpriteMaterial;
    material.map?.dispose();
    material.map = texture;
    material.needsUpdate = true;
  }

  /**
   * A01a/A01b — pick the correct view (front / back / side ± mirror) AND
   * the current animation frame and rebuild the texture when either
   * changed since the last render. Falls back to still-frame views when
   * no animation is loaded for the current state, and to the front view
   * when a specific directional PNG is absent.
   */
  private refreshFrame(instanceId: string, rim: RimStyle | null): void {
    const record = this.sprites.get(instanceId);
    if (!record || !record.authored) return;

    const cam = this.camera;
    let selection: { tag: SpriteViewTag; mirror: boolean };
    if (cam && record.authored.manifest.directional === true) {
      const cameraRelTo = cardinalFromTo(
        { x: record.sprite.position.x, z: record.sprite.position.z },
        { x: cam.position.x, z: cam.position.z },
      );
      selection = selectView(record.facing, cameraRelTo);
    } else {
      selection = { tag: "front", mirror: false };
    }

    const prev = record.lastView;
    const viewUnchanged =
      prev !== undefined && prev.tag === selection.tag && prev.mirror === selection.mirror;
    const frameUnchanged = record.lastFrame === record.frameIndex;
    if (viewUnchanged && frameUnchanged) return;

    // Resolve which frame to render: prefer the loaded animation; fall
    // back to the still-frame `views` bundle when the animation isn't
    // loaded (yet) or has no frames for the selected view.
    const anim =
      record.authored.animations.get(record.currentAnim) ?? record.authored.animations.get("idle");
    let img: HTMLImageElement | null = null;
    if (anim) {
      img = pickFrame(anim, selection.tag, record.frameIndex);
    }
    if (!img) {
      const views = record.authored.views;
      if (selection.tag === "back") img = views.back ?? views.front;
      else if (selection.tag === "side") img = views.side ?? views.front;
      else img = views.front;
    }
    if (!img) return;

    const source: CanvasImageSource = selection.mirror ? mirrorHorizontally(img) : img;

    // A04 — per-frame alignment offset. Key matches the PNG stem
    // (`<anim>_<view>_<frameIndex>`). For mirrored side frames, the sprite
    // body is horizontally flipped, so we flip the authored `x` too so the
    // shift tracks the same side of the body.
    const authoredAnim = anim?.name ?? record.currentAnim;
    // When no animation is loaded we fell back to the first-frame `views`
    // bundle; index that as frame 0 regardless of record.frameIndex.
    const alignFrameIdx = anim ? record.frameIndex : 0;
    const rawAlign = record.authored.align.get(
      alignKey(authoredAnim, selection.tag, alignFrameIdx),
    );
    const align =
      rawAlign === undefined
        ? { x: 0, y: 0 }
        : { x: selection.mirror ? -rawAlign.x : rawAlign.x, y: rawAlign.y };
    const texture = makeAuthoredTexture(source, rim, align);
    const material = record.sprite.material as THREE.SpriteMaterial;
    material.map?.dispose();
    material.map = texture;
    material.needsUpdate = true;
    record.lastView = selection;
    record.lastFrame = record.frameIndex;
  }

  /**
   * A01b — advance the per-record frame clock by `delta` seconds. When
   * the accumulated time exceeds `1 / fps`, increment the frame index;
   * looping animations wrap back to 0, non-looping ones clamp to the
   * final frame (attack holds on impact; death freezes on the corpse).
   * No-op when no animation is loaded for the current state.
   */
  private advanceFrameClock(record: SpriteRecord, delta: number): void {
    if (!record.authored) return;
    const anim = record.authored.animations.get(record.currentAnim);
    if (!anim) return;
    const { fps, frames, loop } = anim.spec;
    record.frameTimer += delta;
    const step = 1 / fps;
    while (record.frameTimer >= step) {
      record.frameTimer -= step;
      record.frameIndex++;
    }
    if (loop) {
      if (record.frameIndex >= frames) record.frameIndex = record.frameIndex % frames;
    } else if (record.frameIndex >= frames) {
      record.frameIndex = frames - 1;
      record.frameTimer = 0; // stop accumulating once clamped
    }
  }

  /**
   * A01b — switch the current animation. Resets the frame clock so the
   * new loop starts on frame 0, and lazy-loads the animation's frame
   * bank when the manifest declares it but it hasn't been fetched yet.
   * No-op when the sprite isn't authored or is already on the requested
   * animation.
   */
  setAnimation(instanceId: string, name: SpriteAnimationName): void {
    const record = this.sprites.get(instanceId);
    if (!record || !record.authored) return;
    if (record.currentAnim === name) return;

    record.currentAnim = name;
    record.frameIndex = 0;
    record.frameTimer = 0;
    record.lastFrame = undefined;

    // Trigger lazy load when the animation is declared but not yet loaded.
    // Idle is preloaded in `loadAuthoredSprite`; everything else waits
    // until first use to keep spawn cost low.
    const spec = record.authored.manifest.animations?.[name];
    if (spec && !record.authored.animations.has(name)) {
      this.lazyLoadAnimation(instanceId, name, spec);
    }
  }

  private lazyLoadAnimation(
    instanceId: string,
    name: SpriteAnimationName,
    spec: { frames: number; fps: number; loop: boolean },
  ): void {
    const record = this.sprites.get(instanceId);
    if (!record || !record.authored) return;
    const pending = record.pendingAnims ?? new Set<SpriteAnimationName>();
    if (pending.has(name)) return;
    pending.add(name);
    record.pendingAnims = pending;

    void loadAnimation(
      record.entityId,
      name,
      spec,
      record.authored.manifest.directional === true,
      record.authored.manifest.disabledFrames,
    ).then((anim) => {
      const after = this.sprites.get(instanceId);
      if (!after || !after.authored) return;
      after.pendingAnims?.delete(name);
      if (!anim) return;
      after.authored.animations.set(name, anim);
      // Force next tick to rebuild the texture with the new frame.
      after.lastFrame = undefined;
    });
  }

  /**
   * A01c — start (or restart) the red hit-flash on the named sprite. A
   * second hit during an in-flight flash refreshes the timer so back-to-
   * back damage keeps tinting rather than snapping back to neutral and
   * re-tinting mid-frame.
   */
  private triggerHitFlash(instanceId: string): void {
    const record = this.sprites.get(instanceId);
    if (!record || record.dying) return;
    record.hitFlashTimer = HIT_FLASH_DURATION;
    const material = record.sprite.material as THREE.SpriteMaterial;
    material.color.setHex(HIT_FLASH_COLOR);
  }

  moveTo(instanceId: string, pos: Position): void {
    const record = this.sprites.get(instanceId);
    if (!record) return;
    // Walking turns the entity toward its direction of travel. Without
    // this, every sprite keeps its spawn-time facing forever and the
    // directional views read as random (mind flayers walking backwards).
    // Facing derives from logical cell deltas — sprite.position carries
    // sub-cell pack offsets, so a slot shuffle must not read as a turn.
    if (record.cell.x !== pos.x || record.cell.z !== pos.z) {
      record.facing = cardinalFromTo(record.cell, pos);
    }
    record.cell = { x: pos.x, z: pos.z };
    this.applyWorldPosition(record);
  }

  /**
   * E-pack — assign a pack member its 2×2 sub-cell corner. The combat
   * manager re-slots every occupant of a cell whenever pack composition
   * changes (join, leave, death); lone occupants return to dead center.
   */
  setSubCellSlot(instanceId: string, slot: number, packCount: number): void {
    const record = this.sprites.get(instanceId);
    if (!record) return;
    const { dx, dz } = subCellOffset(slot, packCount);
    if (record.subCell.dx === dx && record.subCell.dz === dz) return;
    record.subCell = { dx, dz };
    this.applyWorldPosition(record);
  }

  /** Recompute sprite + shadow world position from cell, sub-cell, and dims. */
  private applyWorldPosition(record: SpriteRecord): void {
    const y = record.authored
      ? billboardDims(record.authored.manifest.sizeClass).y
      : record.size === "small"
        ? SPRITE_Y_SMALL
        : SPRITE_Y;
    const x = record.cell.x + record.subCell.dx;
    const z = record.cell.z + record.subCell.dz;
    record.sprite.position.set(x, y, z);
    record.shadow.position.set(x, SHADOW_Y, z);
  }

  /**
   * Turn an entity to face a world position — the combat manager points
   * engaged enemies at the player every tick so stationary bosses track
   * the party instead of attacking in profile. No-op when the target is
   * the entity's own cell.
   */
  faceToward(instanceId: string, target: Position): void {
    const record = this.sprites.get(instanceId);
    if (!record) return;
    const p = record.sprite.position;
    if (p.x === target.x && p.z === target.z) return;
    record.facing = cardinalFromTo({ x: p.x, z: p.z }, target);
  }

  /**
   * Returns the live world-space position of a sprite, or null if unknown.
   * Consumers (EnemyMeter) use this on every RAF tick to project enemy
   * positions to screen coordinates for floating cooldown bars.
   *
   * The returned vector is the sprite's internal `position` — callers must
   * not mutate it.
   */
  getSpritePos(instanceId: string): THREE.Vector3 | null {
    const record = this.sprites.get(instanceId);
    return record ? record.sprite.position : null;
  }

  /**
   * Dev/test probe — per-sprite render state (authored vs procedural,
   * cell, sub-cell slot, scale). Consumed by the `window.__bkSprites`
   * dev global so scripted playtests can assert pack layout.
   */
  debugState(): ReadonlyArray<{
    instanceId: string;
    entityId: string;
    authored: boolean;
    cell: Position;
    subCell: { dx: number; dz: number };
    scale: { x: number; y: number };
  }> {
    return [...this.sprites.entries()].map(([instanceId, r]) => ({
      instanceId,
      entityId: r.entityId,
      authored: r.authored !== undefined,
      cell: { ...r.cell },
      subCell: { ...r.subCell },
      scale: { x: r.sprite.scale.x, y: r.sprite.scale.y },
    }));
  }

  startDeathAnimation(instanceId: string): void {
    const record = this.sprites.get(instanceId);
    if (!record || record.dying) return;
    record.dying = true;
    record.fadeTimer = DEATH_FADE_DURATION;
    // Remove from interactive layer so the enemy can no longer be clicked
    record.sprite.layers.set(0);
  }

  tick(delta: number): void {
    for (const [instanceId, record] of this.sprites) {
      // A01a/A01b — advance the frame clock and refresh the texture as
      // the camera orbits or the animation steps forward. `refreshFrame`
      // is a no-op when neither view nor frame changed.
      if (record.authored && !record.dying) {
        this.advanceFrameClock(record, delta);
        const rim =
          record.npcId !== undefined ? this.npcRim(instanceId) : this.enemyRim(instanceId);
        this.refreshFrame(instanceId, rim);
      }

      // A01c — tick the hit-flash and restore the neutral material color
      // once the window expires. Keeps dying sprites' flash running so a
      // killing blow reads clearly, but the death fade overrides via opacity.
      if (record.hitFlashTimer > 0) {
        record.hitFlashTimer -= delta;
        if (record.hitFlashTimer <= 0) {
          record.hitFlashTimer = 0;
          const material = record.sprite.material as THREE.SpriteMaterial;
          material.color.setHex(0xffffff);
        }
      }

      if (!record.dying) continue;

      record.fadeTimer -= delta;
      const t = Math.max(0, record.fadeTimer / DEATH_FADE_DURATION);

      (record.sprite.material as THREE.SpriteMaterial).opacity = t;
      if (SPRITE_SHADOWS_ENABLED) {
        (record.shadow.material as THREE.MeshBasicMaterial).opacity = t * 0.4;
      }

      if (record.fadeTimer <= 0) {
        this.scene.remove(record.sprite);
        this.scene.remove(record.shadow);
        this.sprites.delete(instanceId);
      }
    }
  }

  dispose(): void {
    for (const [instanceId, record] of this.sprites) {
      this.scene.remove(record.sprite);
      this.scene.remove(record.shadow);
      cellOccupancyStore.remove(instanceId);
    }
    this.sprites.clear();
    for (const un of this.unsubscribes) un();
    this.unsubscribes.length = 0;
  }
}
